import Icon from '@/components/icon';
import Menu from '@/components/menu';
import Text from '@/components/text';
import Tooltip from '@/components/tooltip';
import useThemeMediaQuery from '@/hooks/use-theme-media-query';
import { Citation, ThreadContext, ThreadDocument } from '@/lib/models/thread';
import { openNewTab } from '@/lib/url-helpers';
import { getBreakpoint } from '@/theme';
import { Box, Stack, Theme } from '@mui/material';
import { FC } from 'react';
import { createUseStyles } from 'react-jss';

interface Props {
  threadContext: ThreadContext;
  citations: Citation[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  contextMenu: {
    maxWidth: 500,
    '& .button:hover': {
      opacity: 0.9,
    },
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 16,
    paddingTop: 16,
  },
  [getBreakpoint('sm')]: {
    contextMenu: {
      maxWidth: 'calc(100vw - 40px)',
    },
  },
}));

const MessageContextMenu: FC<Props> = ({ citations, threadContext }) => {
  const styles = useStyles();
  const { small } = useThemeMediaQuery();

  const TriggerEl = (
    <Tooltip title="Cited Context Sources" placement="bottom">
      <Icon name="subject" />
    </Tooltip>
  );

  return (
    <Menu
      trigger={TriggerEl}
      title="Cited Context Sources"
      anchorOrigin={{ vertical: -5, horizontal: 0 }}
      transformOrigin={{ vertical: 'bottom', horizontal: small ? 'right' : 'left' }}
      className={styles.contextMenu}
      hideClose
    >
      <Box width="100%" px={1} boxSizing="border-box">
        <Stack gap={0.5} justifyContent="center" maxWidth="100%">
          {citations.map(({ citationId, messageIndex }) => {
            const source = threadContext.get(messageIndex);

            if (!source) {
              console.warn('missing citation', messageIndex, threadContext);
              return null;
            }

            const { name } = source;
            const docUrl = (source as ThreadDocument).url;
            const hasUrl = !!docUrl;

            return (
              <Stack
                direction="row"
                justifyContent="flex-start"
                gap={0.5}
                key={citationId}
                maxWidth="100%"
              >
                <Box>
                  <Text size="small">{citationId}.</Text>
                </Box>
                <Box maxWidth={small ? '84%' : '90%'}>
                  <Text dotdot size="small">
                    {name}
                  </Text>
                </Box>
                {hasUrl && (
                  <Box>
                    <Icon name="open-tab" size="small" onClick={() => openNewTab(docUrl)} />{' '}
                  </Box>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Menu>
  );
};

export default MessageContextMenu;
